/**
 * Cart-recovery resume page.
 *
 * Customer arrives via the email link (`/r/:orderId?token=...`). We:
 * 1. Validate the token by fetching `/api/orders/:id/resume-info?token=...`.
 *    Wrong token → friendly "link expired/invalid" UI.
 * 2. If already paid → redirect to `/order?session_id=resume&order_id=...`
 *    so the existing Done flow shows the receipt.
 * 3. Else mount Stripe Embedded Checkout via the resume-checkout-session
 *    endpoint. After payment, Stripe redirects back to `/order?...` and
 *    PaymentReturnGuard / Done step take over (already wired up).
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Loader2, AlertCircle } from "lucide-react";

import api from "../lib/api";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function ResumeOrderPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!orderId || !token) {
        setError("This link is missing a token. Please use the most recent email we sent you.");
        setLoading(false);
        return;
      }
      try {
        const r = await api.get(`/orders/${orderId}/resume-info`, { params: { token } });
        if (cancelled) return;
        setInfo(r.data);
        if (r.data.payment_status === "paid") {
          // Already paid — bounce to receipt.
          window.location.replace(`/order?session_id=resume&order_id=${orderId}`);
          return;
        }
        // Spin up a fresh Stripe checkout session against this order.
        const cs = await api.post(
          `/orders/${orderId}/resume-checkout-session`,
          { origin_url: window.location.origin },
          { params: { token } },
        );
        if (cancelled) return;
        if (cs.data?.already_paid) {
          window.location.replace(`/order?session_id=resume&order_id=${orderId}`);
          return;
        }
        setClientSecret(cs.data.client_secret);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 403) {
          setError("This finish-your-order link has expired or doesn't match. Reply to the email and we'll send a fresh one.");
        } else if (e?.response?.status === 404) {
          setError("We couldn't find that order. It may have been removed.");
        } else {
          setError(detail || "Something went wrong loading your order. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "#f7faf6" }}>
      <div className="max-w-lg w-full bg-white rounded-2xl border border-[var(--brand-border)] p-6 sm:p-8">
        <div className="text-center mb-6">
          <p className="text-xs font-bold uppercase tracking-wider brand-text mb-1">Finish your ScreenzZ order</p>
          {info?.order_number && (
            <p className="text-sm muted-text">Order <strong>{info.order_number}</strong></p>
          )}
        </div>

        {loading && (
          <div className="flex items-center gap-2 justify-center py-12 muted-text" data-testid="resume-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Re-opening your checkout…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border-2 p-5 text-center" style={{ background: "#fef2f2", borderColor: "#fecaca" }} data-testid="resume-error">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" style={{ color: "#b91c1c" }} />
            <p className="text-sm text-red-800">{error}</p>
            <a href="/order" className="inline-block mt-4 text-xs font-semibold brand-text underline">Start a new order →</a>
          </div>
        )}

        {!loading && !error && info && (
          <>
            <div className="rounded-2xl bg-[#f7faf6] border border-[var(--brand-border)] p-4 mb-4" data-testid="resume-summary">
              <div className="flex items-center justify-between text-sm">
                <span className="muted-text">{info.product_name || "Your order"}</span>
                <span>×{info.quantity || 1}</span>
              </div>
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--brand-border)]">
                <span className="font-semibold">Total</span>
                <span className="font-bold tabular-nums">{money(info.pricing?.total)}</span>
              </div>
              {info.customer_email_masked && (
                <p className="text-[11px] muted-text mt-2">Saved to {info.customer_email_masked}</p>
              )}
            </div>

            {clientSecret && stripePromise ? (
              <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)]" data-testid="resume-embedded">
                <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : (
              <p className="text-center muted-text text-sm py-4">Loading payment form…</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
